/**
 * A TypeScript port of TeXcount's counting algorithm (version 3.1.1 of `texcount.pl`,
 * Einar Andreas Rødland, LaTeX Project Public License), so a .tex file gets the numbers
 * the TeXcount command line would give it.
 *
 * What is ported: the tokenizer (comments, macros, words, `$`), the parser states and
 * the macro, environment and package rule tables (`texcount-rules.ts`), the preamble and
 * `\begin{document}` handling, floats and captions, headers, inline and displayed math,
 * `%TC:` instructions (ignore, break, macro, envir, macroword), per-section subcounts
 * and word frequencies.
 *
 * What is not: following `\input`/`\include` into other files (there are no other files
 * in a text box; the names are reported instead), `\bibliography` and .bbl inclusion,
 * `%TC:subst`, `%TC:newcounter` and the other less-used instructions, and the colour
 * output. See `docs` in the tool page for the differences.
 */

import {
  addPackage,
  CNT,
  defaultRules,
  type MacroRule,
  type RuleSet,
  ST,
  stateOf,
} from "@/lib/tool-kit/latex-dev/texcount-rules";

export type WordKind = "text" | "header" | "other";

export interface CountSpan {
  start: number;
  end: number;
  kind: WordKind;
}

/** One section's counts (the text before the first section is `_top_`). */
export interface Subcount {
  title: string;
  words: number;
  headerWords: number;
  otherWords: number;
  headers: number;
  floats: number;
  inlineMath: number;
  displayMath: number;
}

export interface TexCountError {
  message: string;
  line: number;
}

export interface TexCountResult {
  /** Words in the running text. */
  words: number;
  /** Words in section headings, \title and the word "Abstract". */
  headerWords: number;
  /** Words in captions, footnotes and other float-like text. */
  otherWords: number;
  /** Number of headings. */
  headers: number;
  /** Number of figures and tables (floats). */
  floats: number;
  inlineMath: number;
  displayMath: number;
  /** `texcount -sum`: text + header + other words + every math formula. */
  sum: number;
  subcounts: Subcount[];
  frequencies: [string, number][];
  spans: CountSpan[];
  errors: TexCountError[];
  /** File names given to \input or \include; not followed. */
  includes: string[];
}

const TOKEN = {
  space: -1,
  comment: 0,
  word: 1,
  symbol: 2,
  macro: 3,
  brace: 4,
  bracket: 5,
  math: 6,
  linebreak: 9,
  tc: 666,
  end: 999,
} as const;

const PARAM = "<param>";
const LETTER_MACROS =
  "ae|AE|o|O|aa|AA|oe|OE|ss|alpha|beta|gamma|delta|epsilon|zeta|eta|theta|iota|kappa|lamda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Upsilon|Phi|Psi|Omega";
const LOGOGRAMS =
  "\\p{Ideographic}\\p{sc=Katakana}\\p{sc=Hiragana}\\p{sc=Thai}\\p{sc=Lao}\\p{sc=Hangul}";
/** A letter: a Unicode letter or digit that is not a logogram. */
const LETTER_CHAR = `(?:(?![${LOGOGRAMS}])[\\p{Nd}\\p{Alphabetic}])`;
const WORD_BOUNDARY = "(?![\\p{L}\\p{N}\\p{M}_])";
const LETTER = `(?:${LETTER_CHAR}|\\\\['"\`~^=](?:${LETTER_CHAR}|\\{${LETTER_CHAR}\\})|\\\\(?:${LETTER_MACROS})(?:\\{\\}|\\s+|${WORD_BOUNDARY}))`;
const WORD = `(?:(?:${LETTER}+|${LETTER}+\\{${LETTER}+\\}|\\{${LETTER}+\\}${LETTER}+)(?:[-'.]?(?:${LETTER}+|\\{${LETTER}+\\}))*|[${LOGOGRAMS}])`;

const RE_WORD = new RegExp(WORD, "uy");
const RE_WORD_FULL = new RegExp(`^${WORD}$`, "u");
const RE_SIMPLE = /[\p{L}\p{N}\p{M}_]/uy;
const RE_SPACE = /[ \t\f]+/y;
const RE_NEWLINE = /\r\n?|\n/y;
const RE_BS_SYMBOL = /\\[{}%]/y;
const RE_BS_MACRO = /\\(?:[a-zA-Z@]+|[^a-zA-Z@\p{Cc}])/uy;
const RE_MATH = /\$\$?/y;
const RE_TC_END = /%+TC:\s*endignore\b[^\r\n]*/iy;
const RE_TC = /%+[tT][cC]:[^\r\n]*/y;
const RE_COMMENT = /%+[^\r\n]*/y;
const RE_OPTION = /\[[^[\]\n]*\]/y;
const RE_OPT_START = /\s*\[/y;
const RE_STAR = /\*/y;
const RE_GOBBLE_NEWLINE = /[ \t\f]*(?:\r\n|\r|\n)[ \t\f]*/y;
const RE_GOBBLE_SPACE = /[ \t\f]+/y;
const RE_TC_START = /%TC:/iy;
const RE_ENV_NAME = /\{([^{}\s]+)\}[ \t\r\f]*/y;
const RE_DOCUMENT = /\{\s*document\s*\}/y;
const RE_DOCCLASS = /\{\s*([^{}\s]+)\s*\}/y;
const RE_BREAK_RUN = /[\s\S]*?(?:\r{2,}|\n{2,}|(?:\r\n){2,})/y;
const RE_DEF_HEAD = /[^{]*\{/y;
const RE_BREAK_LABEL = /\*?(?:\s*\[.*?\])*\s*\{((?:.|\{.*\})*)\}/y;
const RE_INPUT_BRACED = /\s*\{([^{}\s]+)\}/y;
const RE_INPUT_BARE = /\s*([^{}%\\\s]+)/y;
const RE_INPUT_ANY = /\s*\{(.+?)\}/y;
const RE_FILE_ARG = /\s*\{([^{}%]*)\}/y;
const RE_DEFS = /^\\(?:def|edef|gdef|xdef)$/;
const RE_SYMBOL_MACRO = /^\\[^\w_]$/;
const RE_TC_INSTR = /^%+TC:\s*(\w+)\s*([\s\S]*)$/i;
const RE_TC_MACRO = /^([\\]*\S+)\s+(\S+)(?:\s+(-?\w+))?/;
const RE_TC_PARAM = /^\[([0-9a-zA-Z,:]*)\]$/;
const RE_NON_SPACE = /\S/y;
const RE_WHITESPACE = /\s+/;
const RE_NUMBER = /^\d+$/;
const RE_PACKAGE_SPLIT = /\s*,\s*/;
const BREAK_LABELS = new Map<string, string>([
  ["\\part", "Part"],
  ["\\chapter", "Chapter"],
  ["\\section", "Section"],
  ["\\subsection", "Subsection"],
]);

type Counts = number[];

function newCounts(): Counts {
  return [0, 0, 0, 0, 0, 0, 0, 0];
}

function stateIsText(state: number): boolean {
  return state >= ST.text;
}

function stateIsParsed(state: number): boolean {
  return state >= ST.text || state === ST.preamble;
}

function stateIsExclude(state: number): boolean {
  return state <= ST.float;
}

function stateIncEnvir(state: number): boolean {
  return state > ST.xx;
}

function stateTextCount(state: number): number | undefined {
  if (state === ST.text) {
    return CNT.text;
  }
  if (state === ST.headerText) {
    return CNT.header;
  }
  return state === ST.floatText ? CNT.other : undefined;
}

const FIRST_PRIORITY = [
  ST.xall,
  ST.xxx,
  ST.xx,
  ST.special,
  ST.float,
  ST.math,
  ST.ignore,
  ST.preamble,
  ST.toFloat,
  ST.toHeader,
  ST.toInline,
  ST.toDisplay,
  ST.floatText,
  ST.headerText,
  ST.text,
];

/** The state to use when a sub-state is entered from `old`. */
function newState(sub: number, old: number): number {
  for (const state of FIRST_PRIORITY) {
    if (old === state || sub === state) {
      return state;
    }
  }
  return old;
}

interface Token {
  text: string;
  type: number;
  start: number;
}

export interface TexCountOptions {
  /** Cap on the number of highlight spans kept (the counts are always complete). */
  maxSpans?: number;
}

class Counter {
  readonly src: string;
  pos = 0;
  chunkEnd = 0;
  view = "";
  eof = false;
  rules: RuleSet = defaultRules();
  readonly packages = new Set<string>();
  readonly breakPoints = new Map<string, string>(BREAK_LABELS);
  readonly errors: TexCountError[] = [];
  readonly includes: string[] = [];
  readonly freq = new Map<string, number>();
  readonly spans: CountSpan[] = [];
  readonly subcounts: { title: string; counts: Counts }[] = [];
  current: { title: string; counts: Counts } = {
    title: "_top_",
    counts: newCounts(),
  };
  total: Counts = newCounts();
  lastToken: Token = { text: "", type: TOKEN.end, start: 0 };
  readonly maxSpans: number;
  private readonly newlines: number[] = [];

  constructor(src: string, options: TexCountOptions) {
    this.src = src;
    this.maxSpans = options.maxSpans ?? 50_000;
    let at = src.indexOf("\n");
    while (at !== -1) {
      this.newlines.push(at);
      at = src.indexOf("\n", at + 1);
    }
    this.loadChunk();
  }

  /* ----------------------------------------------------------- Input access */

  lineOf(offset: number): number {
    let low = 0;
    let high = this.newlines.length;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (this.newlines[mid] < offset) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    return low + 1;
  }

  error(message: string): void {
    if (this.errors.length < 100) {
      this.errors.push({ message, line: this.lineOf(this.pos) });
    }
  }

  /** TeXcount reads a paragraph at a time; options and spaces are only gobbled inside it. */
  private loadChunk(): boolean {
    if (this.pos >= this.src.length) {
      return false;
    }
    RE_BREAK_RUN.lastIndex = this.pos;
    const found = RE_BREAK_RUN.exec(this.src);
    this.chunkEnd = found ? this.pos + found[0].length : this.src.length;
    this.view = this.src.slice(0, this.chunkEnd);
    return true;
  }

  /** Sticky match at the cursor, inside the current paragraph. */
  match(re: RegExp): RegExpExecArray | null {
    re.lastIndex = this.pos;
    return re.exec(this.view);
  }

  take(re: RegExp): RegExpExecArray | null {
    const found = this.match(re);
    if (found) {
      this.pos += found[0].length;
    }
    return found;
  }

  /** Skip spaces, one line break and comments, as TeXcount does before an option. */
  gobbleSpace(): void {
    for (;;) {
      if (!this.take(RE_GOBBLE_NEWLINE)) {
        this.take(RE_GOBBLE_SPACE);
      }
      if (this.match(RE_TC_START)) {
        return;
      }
      if (!this.take(RE_COMMENT)) {
        return;
      }
    }
  }

  /* -------------------------------------------------------------- Tokenizer */

  private token(text: string, type: number, start: number): Token {
    this.lastToken = { text, type, start };
    return this.lastToken;
  }

  private charToken(type: number): Token {
    const start = this.pos;
    const ch = String.fromCodePoint(this.src.codePointAt(this.pos) ?? 0);
    this.pos += ch.length;
    return this.token(ch, type, start);
  }

  private rawToken(re: RegExp, type: number): Token | null {
    const start = this.pos;
    const found = this.take(re);
    return found ? this.token(found[0], type, start) : null;
  }

  private simpleToken(): Token | null {
    const start = this.pos;
    const found = this.take(RE_SIMPLE);
    if (!found) {
      return null;
    }
    const isWord = RE_WORD_FULL.test(found[0]);
    return this.token(found[0], isWord ? TOKEN.word : TOKEN.symbol, start);
  }

  private backslashToken(): Token {
    return (
      this.rawToken(RE_BS_SYMBOL, TOKEN.symbol) ??
      this.rawToken(RE_BS_MACRO, TOKEN.macro) ??
      this.charToken(TOKEN.end)
    );
  }

  private percentToken(): Token {
    const start = this.pos;
    if (this.take(RE_TC_END)) {
      this.token("%TC:endignore", TOKEN.tc, start);
      return this.lastToken;
    }
    return (
      this.rawToken(RE_TC, TOKEN.tc) ??
      this.rawToken(RE_COMMENT, TOKEN.comment) ??
      this.charToken(TOKEN.comment)
    );
  }

  private rawNextToken(simple: boolean): Token | null {
    while (!this.eof) {
      if (this.pos >= this.chunkEnd) {
        if (!this.loadChunk()) {
          this.eof = true;
        }
        continue;
      }
      const ch = this.src[this.pos];
      if (ch === " " || ch === "\t" || ch === "\f") {
        return this.rawToken(RE_SPACE, TOKEN.space);
      }
      if (ch === "\n" || ch === "\r") {
        return this.rawToken(RE_NEWLINE, TOKEN.linebreak);
      }
      const word = simple
        ? this.simpleToken()
        : this.rawToken(RE_WORD, TOKEN.word);
      if (word) {
        return word;
      }
      return this.punctuationToken(ch);
    }
    return null;
  }

  private punctuationToken(ch: string): Token {
    if (ch === "\\") {
      return this.backslashToken();
    }
    if (ch === "$") {
      return this.rawToken(RE_MATH, TOKEN.math) as Token;
    }
    if (ch === "{" || ch === "}") {
      return this.charToken(TOKEN.brace);
    }
    if (ch === "[" || ch === "]") {
      return this.charToken(TOKEN.bracket);
    }
    if (ch === "%") {
      return this.percentToken();
    }
    return this.charToken(TOKEN.end);
  }

  /** The next token that is not a comment or a line break. */
  nextToken(simple: boolean): Token | null {
    let token = this.rawNextToken(simple);
    while (
      token !== null &&
      (token.type === TOKEN.comment || token.type === TOKEN.linebreak)
    ) {
      token = this.rawNextToken(simple);
    }
    return token;
  }

  /* ------------------------------------------------------------- Counting */

  inc(counter: number, by = 1): void {
    this.current.counts[counter] += by;
  }

  nextSubcount(title: string): void {
    this.subcounts.push(this.current);
    this.current = { title, counts: newCounts() };
  }

  /* ---------------------------------------------------------------- Parser */

  parseAll(): void {
    while (!this.eof) {
      this.parseUnit(ST.text);
    }
  }

  private transition(state: number): number {
    switch (state) {
      case ST.toHeader:
        this.inc(CNT.headers);
        return ST.headerText;
      case ST.toInline:
        this.inc(CNT.inline);
        return ST.math;
      case ST.toDisplay:
        this.inc(CNT.display);
        return ST.math;
      case ST.toFloat:
        this.inc(CNT.floats);
        return ST.float;
      default:
        return state;
    }
  }

  private countWord(token: Token, state: number): void {
    const counter = stateTextCount(state);
    if (counter === undefined) {
      return;
    }
    this.inc(counter);
    const key = token.text.toLowerCase();
    this.freq.set(key, (this.freq.get(key) ?? 0) + 1);
    if (this.spans.length < this.maxSpans) {
      let kind: WordKind = "text";
      if (counter === CNT.header) {
        kind = "header";
      } else if (counter === CNT.other) {
        kind = "other";
      }
      this.spans.push({
        start: token.start,
        end: token.start + token.text.length,
        kind,
      });
    }
  }

  /**
   * Parse one unit in `state`: with `end` set, everything up to that token; with
   * `PARAM`, a single token or {group}; with no `end`, one token.
   */
  parseUnit(initial: number, endToken?: string): string[] {
    let state = this.transition(initial);
    const simple = endToken === PARAM;
    const end = simple ? undefined : endToken;
    const specarg: string[] = [];
    for (;;) {
      const token = this.nextToken(simple);
      if (token === null) {
        break;
      }
      if (end !== undefined && end === token.text) {
        return specarg;
      }
      if (
        state === ST.preamble &&
        token.text === "\\begin" &&
        this.match(RE_DOCUMENT)
      ) {
        state = ST.text;
      }
      this.handleToken(token, state, end, simple, specarg);
      if (end === undefined) {
        return specarg;
      }
    }
    if (end !== undefined) {
      this.error(`Reached end of file while waiting for ${end}.`);
    }
    return specarg;
  }

  private handleToken(
    token: Token,
    state: number,
    end: string | undefined,
    simple: boolean,
    specarg: string[]
  ): void {
    const text = token.text;
    if (state === ST.xall) {
      return;
    }
    if (token.type === TOKEN.space) {
      return;
    }
    if (text === "{") {
      specarg.push(...this.parseUnit(state, "}"));
      return;
    }
    if (text === "}") {
      this.error("Encountered } without corresponding {.");
      return;
    }
    if (token.type === TOKEN.tc) {
      this.parseTc(text);
      return;
    }
    if (state === ST.special) {
      specarg.push(text);
      return;
    }
    if (token.type === TOKEN.word) {
      this.countWord(token, state);
      return;
    }
    if (state === ST.xxx) {
      return;
    }
    this.handleOther(token, state, end, simple);
  }

  private handleOther(
    token: Token,
    state: number,
    end: string | undefined,
    simple: boolean
  ): void {
    const text = token.text;
    if (text === "\\documentclass") {
      this.parseDocumentClass();
    } else if (token.type === TOKEN.macro) {
      this.parseMacro(text, state);
    } else if (text === "$") {
      this.parseMath(state, CNT.inline, "$");
    } else if (text === "$$") {
      if (!(end !== undefined && end === "$")) {
        this.parseMath(state, CNT.display, "$$");
      }
    } else if (simple) {
      // A parameter symbol: ignored.
    }
  }

  private parseDocumentClass(): void {
    this.gobbleOption();
    if (this.take(RE_DOCCLASS)) {
      // class-specific rules are not part of the port
    } else {
      this.parseUnit(ST.ignore);
      this.error("Could not identify document class.");
    }
    while (!this.eof) {
      this.parseUnit(ST.preamble);
    }
  }

  private parseMath(state: number, counter: number, end: string): void {
    if (stateIsText(state)) {
      this.inc(counter);
    }
    this.parseUnit(ST.math, end);
  }

  /* ------------------------------------------------------------- Macros */

  private gobbleOption(): string | null {
    this.gobbleSpace();
    const found = this.take(RE_OPTION);
    return found ? found[0] : null;
  }

  private gobbleOptions(): void {
    while (this.gobbleOption() !== null) {
      // keep gobbling
    }
  }

  /** An optional `[...]` argument, read in the state its rule names. */
  private parseOptionalParam(state: number, old: number): void {
    if (this.take(RE_OPT_START)) {
      this.gobbleSpace();
      this.parseUnit(newState(state, old), "]");
    }
  }

  private gobbleMacroParams(
    rule: MacroRule,
    old: number,
    handler?: (specarg: string[]) => void
  ): void {
    const params = Array.isArray(rule)
      ? rule
      : new Array<number>(rule).fill(ST.ignore);
    const n = params.length;
    if (n > 0) {
      this.take(RE_STAR);
    }
    let auto = true;
    for (let j = 0; j < n; j += 1) {
      const p = params[j];
      if (p === ST.option) {
        j += 1;
        this.parseOptionalParam(params[j], old);
      } else if (p === ST.noOption || p === ST.autoOption) {
        auto = p === ST.autoOption;
      } else {
        if (auto) {
          this.gobbleOptions();
        }
        const specarg = this.parseUnit(newState(p, old), PARAM);
        if (p === ST.special && handler) {
          handler(specarg);
        }
      }
    }
    if (auto) {
      this.gobbleOptions();
    }
  }

  private countMacro(name: string, state: number): boolean {
    const def = this.rules.macroCount.get(name);
    if (def === undefined) {
      return false;
    }
    if (Array.isArray(def)) {
      for (const counter of def) {
        this.inc(counter === CNT.text ? (stateTextCount(state) ?? 0) : counter);
      }
    } else {
      const counter = stateTextCount(state);
      if (counter !== undefined) {
        this.inc(counter, def);
      }
    }
    return true;
  }

  private includePackages(specarg: string[]): void {
    for (const name of specarg.join("").split(RE_PACKAGE_SPLIT)) {
      if (name !== "" && !this.packages.has(name)) {
        this.packages.add(name);
        addPackage(this.rules, name);
      }
    }
  }

  private parseIncludeFile(kind: string): void {
    let name: string | undefined;
    if (kind === "input") {
      const found =
        this.take(RE_INPUT_BRACED) ??
        this.take(RE_INPUT_BARE) ??
        this.take(RE_INPUT_ANY);
      if (!found) {
        this.error("Failed to identify file name.");
        return;
      }
      name = found[1];
    } else {
      const found = this.take(RE_FILE_ARG);
      if (!found) {
        this.error("Failed to identify file parameter {file}.");
        return;
      }
      name = found[1];
    }
    this.includes.push(name);
  }

  private parseMacro(name: string, state: number): void {
    const label = this.breakPoints.get(name);
    if (label !== undefined) {
      const found = this.match(RE_BREAK_LABEL);
      this.nextSubcount(found ? `${label}: ${found[1]}` : label);
    }
    const rules = this.rules;
    if (name === "\\begin" && stateIncEnvir(state)) {
      this.parseEnvir(state);
    } else if (name === "\\end" && stateIncEnvir(state)) {
      this.error("Encountered \\end without corresponding \\begin");
    } else if (name === "\\verb") {
      this.parseVerb();
    } else if (stateIsParsed(state) && rules.packageInc.has(name)) {
      this.gobbleMacroParams(
        rules.packageInc.get(name) as MacroRule,
        ST.text,
        (specarg) => this.includePackages(specarg)
      );
    } else if (stateIsParsed(state) && rules.fileInclude.has(name)) {
      this.countMacro(name, state);
      this.parseIncludeFile(rules.fileInclude.get(name) as string);
    } else if (state === ST.float && rules.floatInc.has(name)) {
      this.gobbleMacroParams(rules.floatInc.get(name) as MacroRule, ST.text);
    } else if (state === ST.preamble && rules.preamble.has(name)) {
      this.countMacro(name, ST.text);
      this.gobbleMacroParams(rules.preamble.get(name) as MacroRule, ST.text);
    } else if (stateIsExclude(state)) {
      this.gobbleOptions();
    } else {
      this.parseOrdinaryMacro(name, state);
    }
  }

  private parseOrdinaryMacro(name: string, state: number): void {
    if (name === "\\(") {
      this.parseMath(state, CNT.inline, "\\)");
    } else if (name === "\\[") {
      this.parseMath(state, CNT.display, "\\]");
    } else if (RE_DEFS.test(name)) {
      this.take(RE_DEF_HEAD);
      this.pos -= 1;
      this.parseUnit(ST.xx);
    } else if (this.rules.macro.has(name)) {
      this.countMacro(name, state);
      this.gobbleMacroParams(this.rules.macro.get(name) as MacroRule, state);
    } else if (this.countMacro(name, state)) {
      // counted as words
    } else if (RE_SYMBOL_MACRO.test(name)) {
      this.gobbleOptions();
    } else {
      this.gobbleOptions();
    }
  }

  private parseVerb(): void {
    this.take(RE_STAR);
    const delimiter = this.match(RE_NON_SPACE);
    if (!delimiter) {
      this.error("Invalid \\verb: delimiter required.");
      return;
    }
    const open = delimiter[0];
    this.pos += open.length;
    const close = this.view.indexOf(open, this.pos);
    if (close === -1) {
      this.error(`Invalid \\verb: could not find ending delimiter (${open}).`);
      return;
    }
    this.pos = close + open.length;
  }

  /* ------------------------------------------------------- Environments */

  private parseEnvir(state: number): void {
    this.gobbleSpace();
    const found = this.take(RE_ENV_NAME);
    if (!found) {
      this.error("Encountered \\begin without environment name provided.");
      return;
    }
    const name = found[1];
    const key = `begin${name}`;
    this.countMacro(key, state);
    const rule = this.rules.macro.get(key);
    if (rule === undefined) {
      this.gobbleOptions();
    } else {
      this.gobbleMacroParams(rule, ST.text);
    }
    const declared = this.rules.envir.get(name);
    const sub = declared === undefined ? state : newState(declared, state);
    if (stateIncEnvir(sub)) {
      this.parseUnit(sub, "\\end");
      this.gobbleSpace();
      const closing = this.take(RE_ENV_NAME);
      if (closing) {
        if (closing[1] !== name) {
          this.error(
            `Environment \\begin{${name}} ended with \\end{${closing[1]}}.`
          );
        }
      } else {
        this.error(`Environment ended while waiting for \\end{${name}}.`);
      }
      return;
    }
    while (!this.eof) {
      this.parseUnit(sub, "\\end");
      const endName = new RegExp(
        `\\s*\\{${escapeRegExp(name)}\\}[ \\t\\r\\f]*`,
        "y"
      );
      if (this.take(endName)) {
        return;
      }
    }
  }

  /* -------------------------------------------------------- %TC commands */

  private parseTc(text: string): void {
    const found = RE_TC_INSTR.exec(text);
    if (!found) {
      return;
    }
    const instr = found[1].toLowerCase();
    const rest = found[2].trim();
    if (instr === "break") {
      this.nextSubcount(rest);
    } else if (instr === "ignore") {
      this.parseUnit(ST.xall, "%TC:endignore");
    } else if (instr === "endignore") {
      this.error("%TC:endignore without corresponding %TC:ignore.");
    } else if (instr === "breakmacro") {
      const parts = rest.split(RE_WHITESPACE);
      if (parts.length >= 2) {
        this.breakPoints.set(parts[0], parts[1]);
      }
    } else {
      this.tcRule(instr, rest);
    }
  }

  private tcRule(instr: string, rest: string): void {
    const parts = RE_TC_MACRO.exec(rest);
    if (!parts) {
      this.error(`Invalid TC command: ${instr}`);
      return;
    }
    const [, macro, param, option] = parts;
    if (instr === "macro") {
      const rule = interpretParam(param, stateOf);
      if (rule !== undefined) {
        this.rules.macro.set(macro, rule);
      }
    } else if (instr === "envir" || instr === "group") {
      const state = option === undefined ? undefined : stateOf(option);
      const rule = interpretParam(param, stateOf);
      if (state === undefined || rule === undefined) {
        this.error(`TC:${instr} requires contents rule specification.`);
        return;
      }
      this.rules.envir.set(macro, state);
      this.rules.macro.set(`begin${macro}`, rule);
    } else if (instr === "macrocount" || instr === "macroword") {
      if (RE_NUMBER.test(param)) {
        this.rules.macroCount.set(macro, Number.parseInt(param, 10));
      }
    } else {
      this.error(`Unsupported TC command: ${instr}`);
    }
  }
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** `[ignore,text]`, a number, or `[option:text]` → a macro rule. */
function interpretParam(
  param: string,
  keymap: (key: string) => number | undefined
): MacroRule | undefined {
  if (RE_NUMBER.test(param)) {
    return Number.parseInt(param, 10);
  }
  const body = RE_TC_PARAM.exec(param);
  if (!body) {
    return undefined;
  }
  const rule: number[] = [];
  const keys = body[1] === "" ? [] : body[1].split(",");
  for (const key of keys) {
    let name = key;
    const colon = key.indexOf(":");
    if (colon !== -1) {
      const prefix = keymap(` ${key.slice(0, colon)}`);
      if (prefix === undefined) {
        return undefined;
      }
      rule.push(prefix);
      name = key.slice(colon + 1);
    }
    const state = keymap(name);
    if (state === undefined) {
      return undefined;
    }
    rule.push(state);
  }
  return rule;
}

function toSubcount(title: string, counts: Counts): Subcount {
  return {
    title,
    words: counts[CNT.text],
    headerWords: counts[CNT.header],
    otherWords: counts[CNT.other],
    headers: counts[CNT.headers],
    floats: counts[CNT.floats],
    inlineMath: counts[CNT.inline],
    displayMath: counts[CNT.display],
  };
}

function isNull(counts: Counts): boolean {
  return counts.slice(1).every((value) => value === 0);
}

/** Count a LaTeX source the way TeXcount does. */
export function texCount(
  source: string,
  options: TexCountOptions = {}
): TexCountResult {
  const counter = new Counter(source, options);
  try {
    counter.parseAll();
  } catch (error) {
    if (!(error instanceof RangeError)) {
      throw error;
    }
    counter.errors.push({
      message: "The source is nested too deeply to read.",
      line: counter.lineOf(counter.pos),
    });
  }
  counter.subcounts.push(counter.current);
  const total = newCounts();
  for (const sub of counter.subcounts) {
    for (let i = 0; i < total.length; i += 1) {
      total[i] += sub.counts[i];
    }
  }
  const frequencies = [...counter.freq.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
  );
  return {
    words: total[CNT.text],
    headerWords: total[CNT.header],
    otherWords: total[CNT.other],
    headers: total[CNT.headers],
    floats: total[CNT.floats],
    inlineMath: total[CNT.inline],
    displayMath: total[CNT.display],
    sum:
      total[CNT.text] +
      total[CNT.header] +
      total[CNT.other] +
      total[CNT.inline] +
      total[CNT.display],
    subcounts: counter.subcounts
      .filter((sub) => !isNull(sub.counts))
      .map((sub) => toSubcount(sub.title, sub.counts)),
    frequencies,
    spans: counter.spans,
    errors: counter.errors,
    includes: counter.includes,
  };
}
